/**
 * User configuration and the shareable config URL (FR-3, FR-28, FR-65).
 *
 * Two things live here: the persisted settings object, and the URL-hash
 * encoding that lets a user reopen their setup on another device or hand it to
 * a league-mate.
 *
 * The hash fragment is not an implementation detail (FR-65). A fragment is
 * never sent to the server — it does not appear in request lines, access logs,
 * or referrer headers — so a shared config URL carrying a league ID stays
 * between the people who have the link. Putting it in a query string would
 * quietly leak every league ID into somebody's logs.
 */

import type { BaselineMethod, EngineConfig, EngineWeights } from '../core/types.ts';
import { DEFAULT_ENGINE_CONFIG } from '../core/types.ts';
import { lsGet, lsSet } from './storage.ts';

export interface AppConfig {
  sleeper_username: string | null;
  sleeper_user_id: string | null;
  league_id: string | null;
  draft_id: string | null;
  season: string;
  /** FR-3: per-field overrides of Sleeper-derived league settings. */
  league_overrides: Record<string, unknown>;
  engine: EngineConfig;
  /** Which source currently sorts the board (FR-16). */
  active_source_id: string;
  /** Per-source blend weights (FR-15). */
  blend_weights: Record<string, number>;
  ui: {
    compact: boolean;
    hide_below_rank: number | null;
    position_filter: string | null;
    show_drafted: boolean;
  };
  /** FR-49: normalized name → Sleeper id, the persistent alias file. */
  aliases: Record<string, string>;
}

export const DEFAULT_CONFIG: AppConfig = {
  sleeper_username: null,
  sleeper_user_id: null,
  league_id: null,
  draft_id: null,
  season: String(new Date().getFullYear()),
  league_overrides: {},
  engine: DEFAULT_ENGINE_CONFIG,
  active_source_id: 'blueprint',
  blend_weights: {},
  ui: { compact: false, hide_below_rank: null, position_filter: null, show_drafted: false },
  aliases: {},
};

const KEY = 'config';

export function loadConfig(): AppConfig {
  const stored = lsGet<Partial<AppConfig>>(KEY, {});
  const storedEngine = stored.engine;
  return {
    ...DEFAULT_CONFIG,
    ...stored,
    // Which source sorts the board is a per-visit choice, not a sticky
    // preference (unlike compact mode, hide-below-rank, etc.): every fresh
    // load of the app opens on Blueprint, even if a previous session ended on
    // a single source. A shared config link (?cfg=) still overrides this,
    // since that's an explicit request to view a specific source.
    active_source_id: DEFAULT_CONFIG.active_source_id,
    engine: {
      ...DEFAULT_ENGINE_CONFIG,
      ...storedEngine,
      weights: { ...DEFAULT_ENGINE_CONFIG.weights, ...(storedEngine?.weights ?? {}) },
      blend_weights: { ...DEFAULT_ENGINE_CONFIG.blend_weights, ...(storedEngine?.blend_weights ?? {}) },
    },
    ui: { ...DEFAULT_CONFIG.ui, ...(stored.ui ?? {}) },
    aliases: { ...(stored.aliases ?? {}) },
    blend_weights: { ...(stored.blend_weights ?? {}) },
  };
}

export function saveConfig(config: AppConfig): void {
  lsSet(KEY, config);
}

// ---------------------------------------------------------------------------
// Shareable config URL (FR-65)
// ---------------------------------------------------------------------------

/** The subset of config that is meaningful to share. */
interface SharedConfig {
  v: 1;
  l: string | null;   // league_id
  d: string | null;   // draft_id
  s: string;          // season
  a: string;          // active_source_id
  w: EngineWeights;   // engine weights
  b: BaselineMethod;
  bw: Record<string, number>; // blend weights
}

/**
 * Encode config into a hash fragment.
 *
 * Deliberately excludes: the user's Sleeper username and user id (identifying),
 * and their alias file (personal corrections, and potentially large).
 */
export function encodeConfigUrl(config: AppConfig, base?: string): string {
  const shared: SharedConfig = {
    v: 1,
    l: config.league_id,
    d: config.draft_id,
    s: config.season,
    a: config.active_source_id,
    w: config.engine.weights,
    b: config.engine.baseline_method,
    bw: config.blend_weights,
  };

  const encoded = base64UrlEncode(JSON.stringify(shared));
  const origin = base ?? (typeof location !== 'undefined' ? location.origin + location.pathname : '');
  return `${origin}#cfg=${encoded}`;
}

export function decodeConfigUrl(hash: string): Partial<AppConfig> | null {
  const match = /[#&]cfg=([^&]+)/.exec(hash);
  if (!match) return null;

  try {
    const parsed = JSON.parse(base64UrlDecode(match[1]!)) as SharedConfig;
    if (parsed.v !== 1) return null;

    return {
      league_id: parsed.l ?? null,
      draft_id: parsed.d ?? null,
      season: parsed.s ?? DEFAULT_CONFIG.season,
      active_source_id: parsed.a ?? DEFAULT_CONFIG.active_source_id,
      blend_weights: parsed.bw ?? {},
      engine: {
        ...DEFAULT_ENGINE_CONFIG,
        weights: { ...DEFAULT_ENGINE_CONFIG.weights, ...(parsed.w ?? {}) },
        baseline_method: parsed.b ?? DEFAULT_ENGINE_CONFIG.baseline_method,
      },
    };
  } catch {
    // A malformed shared link should land the user on a clean onboarding
    // screen, not an error page.
    return null;
  }
}

function base64UrlEncode(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(text: string): string {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

// ---------------------------------------------------------------------------
// Weights (FR-28: hot-reloadable, no restart mid-draft)
// ---------------------------------------------------------------------------

export function normalizeWeights(weights: EngineWeights): EngineWeights {
  const clamp = (n: number): number => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);
  return {
    vor: clamp(weights.vor),
    rank: clamp(weights.rank),
    scarcity: clamp(weights.scarcity),
    need: clamp(weights.need),
    availability: clamp(weights.availability),
    bye: clamp(weights.bye),
    lookahead: clamp(weights.lookahead),
  };
}

/**
 * §8: "If the user sets w2=1.0 and all other weights to 0, the tool must
 * degenerate cleanly to 'just follow the Winks list'." This is that preset,
 * offered in the UI so the escape hatch is one click rather than six sliders.
 */
export const FOLLOW_THE_LIST_WEIGHTS: EngineWeights = {
  vor: 0,
  rank: 1,
  scarcity: 0,
  need: 0,
  availability: 0,
  bye: 0,
  lookahead: 0,
};
